import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import PhotoGrid from '@/components/PhotoGrid';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import ContentSection from '@/components/ContentSection';
import Pagination from '@/components/Pagination';
import { api } from '@/lib/apiClient';
import { Album, Photo } from '@/types/interface';
import { useClientMount } from '@/hooks/useClientMount';

const AlbumPhotos = () => {
    const { albumId } = useParams();
    const [album, setAlbum] = useState<Album | null>(null);
    const [paginatedPhotos, setPaginatedPhotos] = useState<Photo[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const hasMounted = useClientMount();

    useEffect(() => {
        if (!hasMounted) return;

        const fetchAlbum = async () => {
            try {
                setLoading(true);
                setError(false);

                const response = await api.albums.getById(Number(albumId), {
                    page: currentPage,
                    limit: itemsPerPage
                });
                const data = response.data;
                setAlbum(data.album);
                setPaginatedPhotos(data.album.photos);
                setTotalPages(data.totalPages);
            } catch (error: unknown) {
                setError(true);
                const errorMessage = error instanceof Error ? error.message : 'Failed to fetch album';
                console.error('Error fetching album:', errorMessage);
            } finally {
                setLoading(false);
            }
        };

        if (albumId) {
            fetchAlbum();
        }
    }, [albumId, currentPage, itemsPerPage, hasMounted]);

    // Don't render anything until the component has mounted on the client
    if (!hasMounted) {
        return null;
    }

    // ✅ Ensure hooks are always executed before returning UI
    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                    <p className="text-xl text-gray-600">Loading album...</p>
                </div>
            </Layout>
        );
    }

    if (error || !album) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                    <p className="text-xl text-gray-600">Failed to load album.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="bg-gray-50 min-h-screen">
                <PageHeader
                    title={album.title}
                    subtitle={album.description || 'Explore the beautiful photos in this album.'}
                />

                <ContentSection maxWidth="6xl" padding="md">
                    <PhotoGrid photos={paginatedPhotos} />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="mt-16">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => setCurrentPage(page)}
                            />
                        </div>
                    )}
                </ContentSection>
            </div>
        </Layout>
    );
};

export default AlbumPhotos;
